import React, { useContext } from 'react'
import {
  AdGroupInput,
  AdgroupSettingsQuery,
  useAdgroupSettingsQuery,
  useUpdateAdgroupMutation,
} from '../../../__generated__/graphql'
import { useSnackbarManager } from '../../providers/snackbarContext'
import { useAppTranslations } from '../useAppTranslations'
import prepareFormValues from '../../prepareFormValues'
import { filterMutationInput } from '../../generateMutationInput'
import PwsSpinner from '../../../components/loader/PwsSpinner'
import { useSidebarContext } from '../../providers/sidebarContext'

type AdgroupContextInterface = [
  AdgroupSettingsQuery['adGroup'],
  (values: Partial<AdGroupInput>) => Promise<void>,
  boolean,
  () => Promise<unknown>,
]
const AdgroupContext = React.createContext<AdgroupContextInterface | null>(null)
AdgroupContext.displayName = 'AdgroupContext'

export const updateAdGroupToDelete = ['costAdjustments', 'targeting', 'campaign', 'budget']

export const AdgroupContextProvider: React.FC<{ adgroupId: string }> = ({ adgroupId, children }) => {
  const [, setSnackbar] = useSnackbarManager()
  const t = useAppTranslations()
  const { reloadItems } = useSidebarContext()

  const {
    data: adgroupData,
    loading: adgroupLoading,
    refetch: refetchAdGroup,
  } = useAdgroupSettingsQuery({
    variables: { id: adgroupId },
    fetchPolicy: 'no-cache', // This prevents Apollo from using cached data and display bad segments
  })

  const [updateAdgroup, { loading: updateAdgroupLoading }] = useUpdateAdgroupMutation()

  const onUpdateAdgroup = async (changes: Partial<AdGroupInput>): Promise<void> => {
    const cleanData = prepareFormValues(changes) as Partial<AdGroupInput>
    if (!adgroupData?.adGroup) return
    // const updatedAdgroup: AdGroupInput = {
    //   ...adgroupData.adGroup,
    //   campaign: undefined,
    //   targeting: undefined,
    //   costAdjustments: undefined,
    //   ...cleanData,
    // } as AdGroupInput

    const adgroupInput = filterMutationInput<AdgroupSettingsQuery['adGroup'], AdGroupInput>(
      adgroupData.adGroup,
      cleanData,
      updateAdGroupToDelete
    )

    await updateAdgroup({
      variables: { adgroup: adgroupInput },
      update: (cache) => {
        cache.evict({ id: 'ROOT_QUERY', fieldName: 'adGroupBudgetHistory' })
        cache.gc()
      },
    })
      .then(() => {
        setSnackbar({ severity: 'success', message: t('adgroup.updated') })
        if ('status' in changes) reloadItems()
      })
      .catch((err) => setSnackbar({ severity: 'error', message: err.message }))
      .then(() => refetchAdGroup())
  }
  if (adgroupLoading) return <PwsSpinner />
  if (!adgroupData) return null
  return (
    <AdgroupContext.Provider value={[adgroupData.adGroup, onUpdateAdgroup, updateAdgroupLoading, refetchAdGroup]}>
      {children}
    </AdgroupContext.Provider>
  )
}

export const useAdgroupContext = (): AdgroupContextInterface => {
  const value = useContext(AdgroupContext)
  if (!value) throw new Error('Value not found in adgroup context')
  return value
}

export const useOptionalAdgroupContext = (): AdgroupContextInterface[0] | null => {
  const value = useContext(AdgroupContext)
  return value?.[0] ?? null
}
